'use client';

import { useState, useCallback, useEffect } from 'react';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription,
} from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    Scissors,
    List,
    Hash,
    ListOrdered,
    ListChecks,
    ListTodo,
    ListPlus,
} from 'lucide-react';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { textSplitterContent } from '../../data/text-splitter-content';

type SplitMethod =
    | 'character'
    | 'word'
    | 'line'
    | 'sentence'
    | 'regex'
    | 'custom';

export default function TextSplitter() {
    const [inputText, setInputText] = useState('');
    const [outputText, setOutputText] = useState('');
    const [splitMethod, setSplitMethod] = useState<SplitMethod>('word');
    const [customDelimiter, setCustomDelimiter] = useState('');
    const [trimWhitespace, setTrimWhitespace] = useState(true);
    const [removeEmpty, setRemoveEmpty] = useState(true);
    const [chunkSize, setChunkSize] = useState(0);
    const [preview, setPreview] = useState(true);

    const splitText = useCallback(() => {
        if (!inputText.trim()) {
            setOutputText('');
            return;
        }

        try {
            let result: string[] = [];
            let text = trimWhitespace ? inputText.trim() : inputText;

            switch (splitMethod) {
                case 'character':
                    result = text.split('');
                    break;

                case 'word':
                    result = text.split(/\s+/);
                    break;

                case 'line':
                    result = text.split(/\r?\n/);
                    break;

                case 'sentence':
                    result = text.split(/(?<=[.!?])\s+/);
                    break;

                case 'regex':
                    if (!customDelimiter) {
                        result = [text];
                        break;
                    }
                    try {
                        const regex = new RegExp(customDelimiter);
                        result = text.split(regex);
                    } catch {
                        setOutputText('Invalid regular expression');
                        return;
                    }
                    break;

                case 'custom':
                    result = customDelimiter
                        ? text.split(customDelimiter)
                        : [text];
                    break;

                default:
                    result = [text];
            }

            if (removeEmpty) {
                result = result.filter((part) => part.trim() !== '');
            }

            // Improved chunk logic
            if (chunkSize > 0) {
                const chunks: string[] = [];
                let buffer = '';

                for (const part of result) {
                    if (
                        buffer.length > 0 &&
                        buffer.length + part.length > chunkSize
                    ) {
                        chunks.push(buffer);
                        buffer = '';
                    }
                    buffer += (buffer ? ' ' : '') + part;
                }

                if (buffer) chunks.push(buffer);
                result = chunks;
            }

            setOutputText(result.join('\n---\n'));
        } catch (error) {
            setOutputText('An error occurred while splitting the text');
        }
    }, [
        inputText,
        splitMethod,
        customDelimiter,
        trimWhitespace,
        removeEmpty,
        chunkSize,
    ]);

    useEffect(() => {
        if (preview) splitText();
    }, [preview, splitText]);

    const getMethodIcon = (method: SplitMethod) => {
        switch (method) {
            case 'character':
                return <Hash className="h-4 w-4 mr-1" />;
            case 'word':
                return <List className="h-4 w-4 mr-1" />;
            case 'line':
                return <ListOrdered className="h-4 w-4 mr-1" />;
            case 'sentence':
                return <ListChecks className="h-4 w-4 mr-1" />;
            case 'regex':
                return <ListTodo className="h-4 w-4 mr-1" />;
            case 'custom':
                return <ListPlus className="h-4 w-4 mr-1" />;
            default:
                return <Scissors className="h-4 w-4 mr-1" />;
        }
    };

    return (
        <div className="container mx-auto px-6 lg:px-8 py-10">

            {/* HEADER */}
            <div className="mb-10">
                <ToolPageIntro title="Text Splitter">
                Split text into multiple parts with our free online tool. Divide text by lines, characters, words, or custom separators for easy processing.
            </ToolPageIntro>
            </div>

            {/* OPTIONS */}
            <Card>
                <CardHeader>
                    <CardTitle>Split Options</CardTitle>
                    <CardDescription>
                        Choose how you want to split your text
                    </CardDescription>
                </CardHeader>

                <CardContent className="space-y-6">
                    <Tabs
                        value={splitMethod}
                        onValueChange={(value) =>
                            setSplitMethod(value as SplitMethod)
                        }
                    >
                        <TabsList className="grid grid-cols-3 md:grid-cols-6">
                            {(
                                [
                                    'character',
                                    'word',
                                    'line',
                                    'sentence',
                                    'regex',
                                    'custom',
                                ] as SplitMethod[]
                            ).map((method) => (
                                <TabsTrigger
                                    key={method}
                                    value={method}
                                    className="flex items-center justify-center text-xs md:text-sm"
                                >
                                    {getMethodIcon(method)}
                                    {method}
                                </TabsTrigger>
                            ))}
                        </TabsList>
                    </Tabs>

                    {(splitMethod === 'regex' ||
                        splitMethod === 'custom') && (
                            <div>
                                <Label className="mb-2 block">
                                    {splitMethod === 'regex'
                                        ? 'Regex Pattern'
                                        : 'Custom Delimiter'}
                                </Label>
                                <input
                                    type="text"
                                    value={customDelimiter}
                                    onChange={(e) =>
                                        setCustomDelimiter(e.target.value)
                                    }
                                    className="w-full border rounded-md px-3 py-2"
                                />
                            </div>
                        )}

                    <div className="flex flex-wrap gap-6">
                        <div className="flex items-center gap-2">
                            <Switch
                                checked={trimWhitespace}
                                onCheckedChange={setTrimWhitespace}
                            />
                            <span>Trim Whitespace</span>
                        </div>

                        <div className="flex items-center gap-2">
                            <Switch
                                checked={removeEmpty}
                                onCheckedChange={setRemoveEmpty}
                            />
                            <span>Remove Empty</span>
                        </div>

                        <div className="flex items-center gap-2">
                            <Switch
                                checked={preview}
                                onCheckedChange={setPreview}
                            />
                            <span>Live Preview</span>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* INPUT / OUTPUT */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-10">
                <div>
                    <Label>Input Text</Label>
                    <Textarea
                        value={inputText}
                        onChange={(e) => setInputText(e.target.value)}
                        className="min-h-[350px]"
                    />
                </div>

                <div>
                    <Label>Split Results</Label>
                    <Textarea
                        value={outputText}
                        readOnly
                        className="min-h-[350px] bg-muted/50"
                    />
                </div>
            </div>

            {/* SEO CONTENT */}
            {/* SEO CONTENT */}
            <section className="mt-16 w-full bg-muted/5 py-12">
                <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
                    <InformationalContent
                        title={textSplitterContent.title}
                        description={textSplitterContent.description}
                        content={textSplitterContent.content}
                        faqs={textSplitterContent.faqs}
                        relatedTools={textSplitterContent.relatedTools}
                    />
                </div>
            </section>
        </div>
    );
}
